import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Field, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Camera, Loader2, CheckCircle2, Share2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function PhotoUpload({ userId, hasPhoto, onDone }) {
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);
  const token = localStorage.getItem("cc_token");
  const src = hasPhoto ? `${BACKEND}/api/bios/${userId}/photo?auth=${token}&v=${ver}` : null;
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/bios/me/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated"); setVer((v) => v + 1); onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not upload"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-4">
      {src ? <img src={src} alt="You" className="w-24 h-24 rounded-full object-cover border-2 border-cc-blue/20" data-testid="bio-photo-img" />
        : <div className="w-24 h-24 rounded-full bg-cc-blue/10 text-cc-blue flex items-center justify-center border-2 border-cc-blue/20" data-testid="bio-photo-placeholder"><Camera className="w-7 h-7" /></div>}
      <label className="inline-flex items-center gap-1.5 text-sm text-cc-blue cursor-pointer hover:underline" data-testid="bio-photo-upload">
        <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp" onChange={onFile} />
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        {hasPhoto ? "Change photo" : "Add a photo"}
      </label>
    </div>
  );
}

export default function MyBio() {
  const [bio, setBio] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/bios/me").then(({ data }) => { setBio(data); setForm(data); })
    .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not load your bio"));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    const missing = (bio?.questions || []).filter((q) => q.required && !(form[q.key] || "").trim());
    if (missing.length) return toast.error(`Please answer: ${missing.map((q) => q.label).join(", ")}`);
    setSaving(true);
    try { const { data } = await api.put("/bios/me", form); toast.success("Bio saved"); await load();
      if (data.completed) toast.success("Your bio is complete 🎉"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  if (err) return <div className="p-6"><EmptyState text={err} /></div>;
  if (!bio) return <div className="p-6"><EmptyState text="Loading…" /></div>;
  const isCI = bio.kind === "ci";

  return (
    <div className="max-w-2xl" data-testid="my-bio-page">
      <PageHeader title="My Bio" subtitle={isCI ? "Fill this out once — we'll use it to introduce you to incoming students and share your site details." : "A short intro about you, shared with the campus team and schools."}>
        {isCI && (
          <Button variant="outline" onClick={() => navigate(`/bios/${bio.user_id}`)} data-testid="preview-bio-btn"><Share2 className="w-4 h-4 mr-1.5" />Preview / share</Button>
        )}
      </PageHeader>

      {bio.completed && (
        <div className="flex items-center gap-2 text-sm text-cc-green mb-4" data-testid="bio-complete-badge"><CheckCircle2 className="w-4 h-4" />Your bio is complete.</div>
      )}

      <Card className="p-6 space-y-5">
        <PhotoUpload userId={bio.user_id} hasPhoto={!!bio.photo_path} onDone={load} />

        {(bio.questions || []).map((q) => (
          <div key={q.key}>
            <label className="block text-sm font-semibold text-zinc-900 mb-0.5">
              {q.label}{q.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {q.help && <p className="text-xs text-zinc-500 mb-2 leading-relaxed">{q.help}</p>}
            {q.type === "textarea"
              ? <Textarea rows={5} value={form[q.key] || ""} onChange={(e) => set(q.key, e.target.value)} data-testid={`bio-${q.key.replace(/_/g, "-")}`} />
              : <Input value={form[q.key] || ""} onChange={(e) => set(q.key, e.target.value)} data-testid={`bio-${q.key.replace(/_/g, "-")}`} />}
          </div>
        ))}

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-cc-blue text-white" data-testid="save-bio-btn">{saving ? "Saving…" : "Save bio"}</Button>
        </div>
      </Card>
    </div>
  );
}
